import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Modal, Select, Toggle } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { Workspace } from "@/types/workspace";
import { useCreateWorkspace, usePlans, useUpdateWorkspace } from "@/hooks/useWorkspaces";
import "./EditWorkspaceModal.css";

type Props =
  | { mode: "create"; onClose: () => void; workspace?: undefined }
  | { mode: "edit"; workspace: Workspace; onClose: () => void };

export function EditWorkspaceModal({ mode, workspace, onClose }: Props) {
  const navigate = useNavigate();
  const { data: plans = [] } = usePlans();
  const createMut = useCreateWorkspace();
  const updateMut = useUpdateWorkspace(workspace?.id ?? 0);

  const [name, setName] = useState(workspace?.name ?? "");
  const [slug, setSlug] = useState("");
  const [planSlug, setPlanSlug] = useState(workspace?.plan?.slug ?? "");
  const [demoMode, setDemoMode] = useState(workspace?.demo_mode ?? false);
  const [error, setError] = useState<string | null>(null);

  const busy = createMut.isPending || updateMut.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (mode === "create") {
        const ws = await createMut.mutateAsync({
          name: name.trim(),
          slug: slug.trim() || undefined,
          plan_slug: planSlug || undefined,
        });
        onClose();
        navigate(`/workspaces/${ws.id}`);
      } else {
        await updateMut.mutateAsync({
          name: name.trim(),
          plan_slug: planSlug || undefined,
          demo_mode: demoMode,
        });
        onClose();
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={mode === "create" ? "Criar workspace" : "Editar workspace"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="edit-ws-form" disabled={busy}>
            {busy ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <form id="edit-ws-form" className="ws-form" onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <label className="form-field">
          <span>Nome</span>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        {mode === "create" && (
          <label className="form-field">
            <span>Slug (opcional)</span>
            <input
              className="form-input"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="gerado a partir do nome"
            />
          </label>
        )}
        <label className="form-field">
          <span>Plano</span>
          <Select
            value={planSlug || null}
            onChange={setPlanSlug}
            options={plans.map((p) => ({ value: p.slug, label: p.name }))}
            placeholder="— sem plano —"
            clearable
            clearLabel="— sem plano —"
          />
        </label>
        {mode === "edit" && (
          <div className="form-field">
            <Toggle
              checked={demoMode}
              onChange={setDemoMode}
              label="Modo demo"
              sub="Workspace de treino/teste — enquanto ligado, não opera (mesmo com contrato)."
            />
          </div>
        )}
      </form>
    </Modal>
  );
}
